
import React, { useState, useEffect, useContext, useRef } from "react";
import Footer from "../components/dashboard/footer";
import Header from "../components/dashboard/header";
import Sidebar from "../components/dashboard/sidebar";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";
import SweetAlert from 'react-bootstrap-sweetalert';
import { contentContext } from "../context/contentContext";
import SessionPopup from "./SessionPopup";
import BankConnectBanner from "./dashboard/BankConnectBanner";
import TableLoader from "../components/tableLoader";
import DataTable from 'react-data-table-component';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { keyActivate } from "../utils/a11y";

let { lambda, appname } = window.app;

const views = ['DAY', 'WEEK', 'MONTH', 'YEAR'];

const STATUS_BARS = [
    { key: 'paid', label: 'Paid', color: '#198754' },
    { key: 'canceled', label: 'Canceled', color: '#6b7a10' },
    { key: 'refundDue', label: 'Refund Due', color: '#fd7e14' },
    { key: 'refund', label: 'Refunded', color: '#dc3545' },
    { key: 'failed', label: 'Failed', color: '#6c757d' },
];

const PLAN_BARS = [
    { key: 'basic', label: 'Basic', color: '#0dcaf0' },
    { key: 'premium', label: 'Premium', color: '#0d6efd' },
    { key: 'vip', label: 'VIP', color: '#ffc107' },
];

const getStatusLabel = (status) => {
    if (!status) return "";
    const s = status.toLowerCase();
    if (s === "success") return "Paid";
    if (s === "refunded") return "Refunded";
    if (s.includes("cancel")) return "Canceled";
    return status;
};

const getStatusBadgeClass = (status) => {
    if (!status) return "badge bg-secondary";
    const s = status.toLowerCase();
    if (s === "success") return "badge bg-success";
    if (s === "refunded") return "badge bg-info";
    if (s.includes("cancel")) return "badge bg-warning text-dark";
    return "badge bg-secondary";
};

// Stripe dispute statuses while the bank is still deciding.
const DISPUTE_OPEN = ["needs_response", "under_review", "warning_needs_response", "warning_under_review"];

// A dispute that ended without being lost: the badge goes back to Paid / Refunded and
// this line says the payment was disputed.
const DISPUTE_CLOSED_NOTES = { won: "Dispute won", warning_closed: "Inquiry closed", prevented: "Dispute prevented" };

const StatusCell = ({ row }) => {
    let label = getStatusLabel(row?.status);
    let badgeClass = getStatusBadgeClass(row?.status);
    // A SpotSaver refund of part of the booking leaves the status Paid.
    if (row?.refundPayment === "partial") {
        label = "Partially Refunded";
        badgeClass = "badge bg-info";
    }
    if (DISPUTE_OPEN.includes(row?.disputeStatus)) {
        label = "Disputed";
        badgeClass = "badge bg-warning text-dark";
    }
    // The guest's bank decided for the guest; the venue's share is taken back.
    if (row?.disputeStatus === "lost") {
        label = "Dispute Lost";
        badgeClass = "badge bg-danger";
    }
    if (!label) return <span>—</span>;
    // What Stripe took back from this venue's share: a SpotSaver refund, or a lost dispute.
    const takenBack = parseFloat(row?.transferReversedAmount);
    const disputeNote = DISPUTE_CLOSED_NOTES[row?.disputeStatus];
    return (
        <div>
            <span className={badgeClass}>{label}</span>
            {disputeNote && (
                <div style={{ fontSize: "11px", color: "#6c757d", marginTop: "2px" }}>
                    {disputeNote}
                </div>
            )}
            {takenBack > 0 && (
                <div style={{ fontSize: "11px", color: "#6c757d", marginTop: "2px" }}>
                    −${takenBack.toFixed(2)} from payout
                </div>
            )}
        </div>
    );
};

const Transactions = () => {
    const navigate = useNavigate();

    const [showSessionPopupup, setShowSessionPopupup] = useState(false);
    const [tableErrMsg, setTableErrMsg] = useState(false);
    const [locationData, setLocationData] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState("");
    const [view, setView] = useState("DAY");
    const [currentDate, setCurrentDate] = useState(new Date());
    const [showCalendar, setShowCalendar] = useState(false);
    const [transactionCounts, setTransactionCounts] = useState({});
    const [chartData, setChartData] = useState([]);
    const [chartViewType, setChartViewType] = useState("status");
    const [totalCount, setTotalCount] = useState(0);
    const [selectedPlanType, setSelectedPlanType] = useState("All");
    const calendarRef = useRef(null);

    const {
        isLoading, setIsLoading, clientInfo,
        activeMenuObj, setActiveMenuObj,
        usersearch, setUserSearch,
        currentPage, setcurrentPage,
        data, setData,
        rowsPerPage, setRowsPerPage,
        currentPageNew, setCurrentPage,
        route, setRoute,
        usePrevious, setActiveMenuId, GetTimeActivity,
        searchedFlag, setSearchedFlag,
    } = useContext(contentContext);

    const useruniqueid = localStorage.getItem("clientid");

    const prevRoute = usePrevious(route);
    useEffect(() => {
        if (prevRoute !== undefined && prevRoute !== route) {
            setCurrentPage(1);
            setRowsPerPage(15);
            setSearchedFlag(false);
            setUserSearch("");
        }
    }, [prevRoute]);

    const keyForRerender = `${rowsPerPage}_${data?.length}`;

    // ── Table columns ────────────────────────────────────────────────
    const columns = [
        {
            name: 'Transaction ID',
            selector: row => row?.transactionId ?? "",
            sortable: true,
            width: "16%",
        },
        {
            name: 'Date & Time of Purchase',
            selector: row => row?.created ?? "",
            cell: (row) => {
                if (!row?.created) return "";
                const tz = row?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                return moment(row.created).tz(tz).format("MM/DD/YY h:mm A");
            },
            sortable: true,
            width: "14%",
        },
        {
            name: 'Party Size',
            selector: row => row?.partySize ?? "",
            sortable: true,
            center: true,
            width: "8%",
        },
        {
            name: 'Unit Price',
            selector: row => row?.unitPrice ?? row?.price ?? "",
            cell: (row) => {
                const val = row?.unitPrice ?? row?.payAmount ?? row?.price;
                return val != null ? `$${Number(val).toFixed(2)}` : "—";
            },
            sortable: true,
            width: "9%",
        },
        {
            name: 'Plan Type',
            selector: row => row?.plan ?? row?.planType ?? "",
            sortable: true,
            width: "9%",
        },
        {
            // clientAmount is the spot price without SpotSaver's service fee - the same
            // figure Payouts shows. totalAmount is the guest's fee-inclusive charge and
            // is only the fallback for a backend that doesn't send clientAmount yet.
            name: 'Total Amount',
            selector: row => row?.clientAmount ?? row?.totalAmount ?? 0,
            cell: (row) => `$${Number(row?.clientAmount ?? row?.totalAmount ?? 0).toFixed(2)}`,
            sortable: true,
            width: "10%",
        },
        {
            name: 'Business Location',
            selector: row => row?.locationName ?? "",
            sortable: true,
            width: "20%",
        },
        {
            name: 'Spot Information',
            selector: row => row?.bookingDate ?? row?.spotDate ?? "",
            cell: (row) => {
                const date = row?.bookingDate ?? row?.spotDate;
                const time = row?.spotstart ?? row?.spotStartTime;
                if (!date && !time) return "—";
                // If date is a full ISO datetime, just parse and format it locally
                if (date && (date.includes('T') || date.includes('Z'))) {
                    const m = moment(date);
                    return m.isValid() ? m.local().format("MM/DD/YY h:mm A") : "—";
                }
                if (date && time) {
                    const cleanTime = String(time).trim();
                    const combined = `${date} ${cleanTime}`;
                    const parsed = moment(combined, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm A", "YYYY-MM-DD hh:mm A", "MM/DD/YYYY HH:mm", "MM/DD/YYYY h:mm A"], true);
                    if (parsed.isValid()) return parsed.format("MM/DD/YY h:mm A");
                    // Parse date and time separately as fallback
                    const dateParsed = moment(date, ["YYYY-MM-DD", "MM/DD/YYYY", "MM-DD-YYYY"], true);
                    const timeParsed = moment(cleanTime, ["HH:mm", "h:mm A", "hh:mm A"], true);
                    if (dateParsed.isValid() && timeParsed.isValid()) {
                        return dateParsed.format("MM/DD/YY") + " " + timeParsed.format("h:mm A");
                    }
                    if (dateParsed.isValid()) return dateParsed.format("MM/DD/YY") + " " + cleanTime;
                    return `${date} ${time}`;
                }
                if (date) {
                    const m = moment(date, ["YYYY-MM-DD", "MM/DD/YYYY"], true);
                    return m.isValid() ? m.format("MM/DD/YY") : date;
                }
                return time;
            },
            sortable: true,
            width: "14%",
        },
        {
            name: 'Status',
            selector: row => row?.status ?? "",
            cell: (row) => <StatusCell row={row} />,
            sortable: true,
            width: "7%",
        },
        {
            name: 'Payout Settled Date',
            selector: row => row?.payoutSettledDate ?? "",
            cell: (row) => {
                // A canceled (or refunded/failed) booking never gets paid out, so its
                // payoutSettledDate - Pending or otherwise - doesn't mean anything here
                // and shouldn't be shown alongside a "Canceled" status.
                if (row?.status && row.status.toLowerCase() !== "success") return "—";
                if (!row?.payoutSettledDate) return "—";
                // Comes through as a plain date OR as a status+date label like
                // "Pending (08-20-2026)" / "Settled (08-20-2026)" - pull the status
                // word AND the date apart so both are shown; handing the whole label
                // to moment can't parse it (shows "Invalid date"), and showing the
                // date alone throws away the one thing that tells Pending from Settled.
                const raw = String(row.payoutSettledDate);
                const match = raw.match(/^(.*?)\s*\((\d{2}-\d{2}-\d{4})\)\s*$/);
                const label = match ? match[1].trim() : null;
                const dateStr = match ? match[2] : raw;
                const parsed = match ? moment(dateStr, "MM-DD-YYYY") : moment(raw);
                const formattedDate = parsed.isValid() ? parsed.format("MM/DD/YY") : dateStr;

                if (!label) return formattedDate;
                const isSettled = label.toLowerCase() === "settled";
                return (
                    <div>
                        <div style={{ color: isSettled ? "#198754" : "#997404", fontWeight: 600, fontSize: "12px" }}>
                            {label}
                        </div>
                        <div style={{ fontSize: "12px" }}>{formattedDate}</div>
                    </div>
                );
            },
            sortable: true,
            width: "7%",
        },

    ];

    // ── Date helpers ─────────────────────────────────────────────────
    const fmtDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const getDateRange = (v, d) => {
        const date = new Date(d);
        if (v === 'DAY') {
            const s = fmtDate(date);
            return { fromDate: s, toDate: s, groupBy: 'day' };
        }
        if (v === 'WEEK') {
            const day = date.getDay();
            const diff = day === 0 ? -6 : 1 - day;
            const mon = new Date(date);
            mon.setDate(date.getDate() + diff);
            const sun = new Date(mon);
            sun.setDate(mon.getDate() + 6);
            return { fromDate: fmtDate(mon), toDate: fmtDate(sun), groupBy: 'week' };
        }
        if (v === 'MONTH') {
            const first = new Date(date.getFullYear(), date.getMonth(), 1);
            const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
            return { fromDate: fmtDate(first), toDate: fmtDate(last), groupBy: 'month' };
        }
        if (v === 'YEAR') {
            return { fromDate: `${date.getFullYear()}-01-01`, toDate: `${date.getFullYear()}-12-31`, groupBy: 'year' };
        }
    };

    const getLabel = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const current = new Date(currentDate);
        current.setHours(0, 0, 0, 0);
        const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

        if (view === 'DAY') {
            const diff = Math.floor((current - today) / 86400000);
            if (diff === 0) return `${current.toDateString()} (Today)`;
            if (diff === -1) return `${current.toDateString()} (Yesterday)`;
            return current.toDateString();
        }
        if (view === 'WEEK') {
            const getMonday = (d) => {
                const dt = new Date(d);
                const day = dt.getDay();
                dt.setDate(dt.getDate() + (day === 0 ? -6 : 1 - day));
                return dt;
            };
            const mon = getMonday(current);
            const sun = new Date(mon);
            sun.setDate(mon.getDate() + 6);
            return `${fmt(mon)} ~ ${fmt(sun)}`;
        }
        if (view === 'MONTH') return current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        if (view === 'YEAR') return current.getFullYear().toString();
        return '';
    };

    const getDatePickerProps = () => {
        if (view === 'WEEK') return { filterDate: (d) => d.getDay() === 1, dateFormat: 'MM/dd/yyyy' };
        if (view === 'MONTH') return { showMonthYearPicker: true, dateFormat: 'MMMM yyyy' };
        if (view === 'YEAR') return { showYearPicker: true, dateFormat: 'yyyy' };
        return { dateFormat: 'MM/dd/yyyy' };
    };

    // ── Calendar outside-click ────────────────────────────────────────
    const handleClickOutside = (e) => {
        if (calendarRef.current && !calendarRef.current.contains(e.target)) {
            setShowCalendar(false);
        }
    };
    useEffect(() => {
        if (showCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCalendar]);

    // ── Mount ────────────────────────────────────────────────────────
    useEffect(() => {
        setRoute("transactions");
        setActiveMenuId(200005);
        setActiveMenuObj({ "Client Management": false, "Contact Management": false, "Reports": false });
        setRowsPerPage(10);
        setData([]);
        userActivity();
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) { navigate("/signin"); return; }
        if (!clientInfo?.subscriptionPayment) return;
        if (clientInfo.subscriptionPayment !== "PAYMENT SUCCESS") navigate("/signin");
    }, [clientInfo, navigate]);

    useEffect(() => {
        getStore();
    }, []);

    useEffect(() => {
        fetchTransactionCounts();
    }, [selectedStoreId, selectedPlanType]);

    useEffect(() => {
        fetchChartData();
    }, [view, currentDate, selectedStoreId, selectedPlanType]);

    useEffect(() => {
        fetchTransactionList();
    }, [currentPage, rowsPerPage, selectedStoreId, view, currentDate, selectedPlanType]);

    // ── User activity ─────────────────────────────────────────────────
    const userActivity = () => {
        const pageName = window.location.pathname.split("/").pop();
        const presentTime = moment();
        const payload = {
            clientid: localStorage.getItem("clientid"),
            pagename: pageName,
            pageurl: window.location.href,
            starttime: presentTime,
            useragent: JSON.parse(localStorage.getItem("loc")),
        };
        const previousId = localStorage.getItem("previousid");
        const urlLink = lambda + '/useractivity?appname=' + appname + (previousId ? "&previousid=" + previousId : "");
        axios({ method: 'POST', url: urlLink, data: payload })
            .then(response => {
                if (response?.data?.statusCode === 200) localStorage.setItem("previousid", response?.data?.result);
            });
    };

    // ── API calls ─────────────────────────────────────────────────────
    const getStore = () => {
        const token = localStorage.getItem("token");
        axios({
            method: 'POST',
            url: `${lambda}/getStores?appname=${appname}&clientid=${useruniqueid}&token=${token}`,
        }).then(response => {
            if (response?.data?.result === "Invalid token or Expired") {
                setShowSessionPopupup(true);
            } else if (response?.data?.result?.length > 0) {
                setLocationData(response?.data?.result);
            }
        });
    };

    const fetchTransactionCounts = async () => {
        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const token = localStorage.getItem("token");
            let url = `${lambda}/transactionCounts?appname=${appname}&clientid=${useruniqueid}&timezone=${encodeURIComponent(timeZone)}&token=${token}`;
            if (selectedStoreId) url += `&storeid=${selectedStoreId}`;
            if (selectedPlanType !== "All") url += `&planType=${selectedPlanType}`;
            const response = await axios.get(url);
            if (response?.data?.result === "Invalid token or Expired") {
                setShowSessionPopupup(true);
            } else {
                setTransactionCounts(response?.data?.result || {});
            }
        } catch (err) {
            console.error("Error fetching transaction counts:", err);
        }
    };

    const fetchChartData = async () => {
        const { fromDate, toDate, groupBy } = getDateRange(view, currentDate);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const token = localStorage.getItem("token");

        const payload = {
            fromDate, toDate, groupBy, timeZone,
            ...(selectedPlanType !== "All" && { planType: selectedPlanType }),
        };

        let analyticsUrl = `${lambda}/transactionAnalytics?appname=${appname}&clientid=${useruniqueid}&token=${token}`;
        if (selectedStoreId) analyticsUrl += `&storeid=${selectedStoreId}`;

        try {
            const response = await axios.post(analyticsUrl, payload);
            if (response?.data?.result === "Invalid token or Expired") {
                setShowSessionPopupup(true);
            } else {
                setChartData(response?.data?.result || []);
            }
        } catch (err) {
            console.error("Error fetching chart data:", err);
        }
    };

    const fetchTransactionList = async () => {
        GetTimeActivity();
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const { fromDate, toDate, groupBy } = getDateRange(view, currentDate);
        const payload = {
            fromDate,
            toDate,
            groupBy,
            timeZone,
        };

        let listUrl = `${lambda}/transactionList?appname=${appname}&clientid=${useruniqueid}&token=${token}&pageNumber=${currentPage}&assetcount=${rowsPerPage}`;
        if (selectedStoreId) listUrl += `&storeid=${selectedStoreId}`;
        if (selectedPlanType !== "All") listUrl += `&planType=${selectedPlanType}`;

        try {
            const response = await axios.post(listUrl, payload);
            if (response?.data?.result === "Invalid token or Expired") {
                setShowSessionPopupup(true);
            } else {
                const result = response?.data?.result;
                setData(result?.transactions || []);
                setTotalCount(result?.totalCount || 0);
                global.exportExcelData = result?.transactions || [];
            }
        } catch (err) {
            console.error("Error fetching transaction list:", err);
            setTableErrMsg(true);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Handlers ──────────────────────────────────────────────────────
    const handlePageChange = (page) => {
        GetTimeActivity();
        setcurrentPage(page);
    };

    const handlePerRowsChange = (newPerPage) => {
        GetTimeActivity();
        setRowsPerPage(newPerPage);
        setcurrentPage(1);
    };

    const handleStoreChange = (e) => {
        setSelectedStoreId(e.target.value);
        setcurrentPage(1);
    };

    const handleViewChange = (selectedView) => {
        setView(selectedView);
        setCurrentDate(new Date());
        setChartData([]);
        setcurrentPage(1);
    };

    const handlePlanTypeChange = (plan) => {
        setSelectedPlanType(plan);
        setcurrentPage(1);
    };

    const customNoRecords = () => (
        <div style={{ textAlign: 'center', padding: '32px 20px' }}>
            <span className="material-icons" style={{ fontSize: '40px', color: '#aaa', display: 'block' }}>summarize</span>
            <p style={{ margin: '10px 0 0', color: '#888', fontSize: '14px' }}>No transactions were found</p>
        </div>
    );

    // ── Summary card config ───────────────────────────────────────────
    const countCards = [
        { label: "Today", value: transactionCounts?.todayTotal, icon: "today" },
        { label: "This Week", value: transactionCounts?.weekTotal, icon: "date_range" },
        { label: "This Month", value: transactionCounts?.monthTotal, icon: "calendar_month" },
        { label: "This Year", value: transactionCounts?.yearTotal, icon: "calendar_today" },
    ];

    // ── Render ────────────────────────────────────────────────────────
    return (
        <>
            {showSessionPopupup && <SessionPopup />}

            <div id="layout-wrapper">
                <Header />
                <Sidebar />

                <div className="main-content user-management Dashboard-page transaction-page-table" id="transaction-main-content">
                    <div className="page-content" id="transaction-page-content">
                        <div className="container-fluid" id="transaction-container-fluid">

                            {/* Breadcrumb */}
                            <div className="row mb-4 breadcrumb" id="transaction-breadcrumb-row">
                                <div className="col-lg-12">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <h4 className="mb-2 card-title" id="transaction-breadcrumb-title">Location Transactions</h4>
                                        </div>
                                    </div>
                                </div>
                                {/* Money earned here is money that can't reach the client until Stripe
                                    has their bank details - so the reminder belongs on the page where
                                    they're looking at those earnings, not only on the Dashboard. */}
                                <BankConnectBanner />
                            </div>

                            {/* Revenue Summary Cards */}
                            <div className="row g-3 admin-dashboard-wrapper Visitor-cards-wrapper mb-4" id="transaction-summary-cards">
                                {countCards.map((card, i) => (
                                    <div className="col-md-3" key={i}>
                                        <div className="card dashboard-box features-box">
                                            <div className="card-body">
                                                <div className="d-flex">
                                                    <div className="flex-grow-1">
                                                        <p className="feu-txt-18">{card.label}</p>
                                                        <h4 className="feu-txt-20">
                                                            {card.value != null ? `$${Number(card.value).toFixed(2)}` : '-'}
                                                        </h4>
                                                    </div>
                                                    <div className="flex-shrink-0 align-self-center">
                                                        <div className="feu-icon-section">
                                                            <span className="material-symbols-outlined">{card.icon}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Chart Section */}
                            <div className="p-4 bg-white shadow-lg rounded-lg Dashboard-wrapper mb-4" id="transaction-chart-wrapper">

                                {/* Filters row */}
                                <div className="row align-items-center gy-3 mb-4" id="transaction-filters">
                                    {/* Store dropdown */}
                                    <div className="col-12 col-sm-6 col-md-4 col-lg-3" id="transaction-filter-location">
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <span className="material-symbols-outlined">store</span>
                                            </span>
                                            <select
                                                className="form-select"
                                                onChange={handleStoreChange}
                                                value={selectedStoreId}
                                                id="transaction-store-select"
                                            >
                                                <option value="">All Locations</option>
                                                {locationData?.map((item) => (
                                                    <option key={item.storeid} value={item.storeid}>{item.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Date label + view toggle */}
                                    <div
                                        className="col-12 col-md-8 col-lg-9 text-md-end d-flex flex-wrap justify-content-md-end align-items-center gap-2"
                                        id="transaction-filter-right"
                                    >
                                        <div className="gap-1 me-2 date-selecting-wrapper" style={{ position: 'relative' }} id="transaction-date-selector">
                                            <span
                                                className="px-3 py-2 rounded-lg text-sm fw-semibold"
                                                onClick={() => setShowCalendar(!showCalendar)}
                                                onKeyDown={keyActivate(() => setShowCalendar(!showCalendar))}
                                                style={{ cursor: 'pointer' }}
                                                id="transaction-date-label"
                                            >
                                                {getLabel()}
                                            </span>
                                            {showCalendar && (
                                                <div
                                                    className="position-absolute bg-white shadow p-2 mt-2 rounded"
                                                    style={{ zIndex: 10, top: 33, right: 0 }}
                                                    id="transaction-calendar-popup"
                                                >
                                                    <div ref={calendarRef}>
                                                        <DatePicker
                                                            className="form-control custom-date"
                                                            inline
                                                            selected={currentDate}
                                                            onChange={(date) => { setCurrentDate(date); setShowCalendar(false); }}
                                                            {...getDatePickerProps()}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="btn-group" role="group" id="transaction-view-selector">
                                            {views.map((v) => (
                                                <button
                                                    key={v}
                                                    className={`btn btn-style ${view === v ? 'active' : ''}`}
                                                    onClick={() => handleViewChange(v)}
                                                    id={`transaction-view-${v.toLowerCase()}`}
                                                >
                                                    {v.charAt(0) + v.slice(1).toLowerCase()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Chart title and view toggle */}
                                <div className="row align-items-center mb-3" id="transaction-chart-title">
                                    <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <h6 className="mb-0 fw-semibold">Transaction Analytics</h6>
                                        <div className="d-flex align-items-center gap-3 flex-wrap">
                                            {/* Plan Type filter */}
                                            <div className="d-flex align-items-center gap-2" id="transaction-plan-type-filter">
                                                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>Plan Type:</span>
                                                {["All", "Basic", "Premium", "VIP"].map((plan) => (
                                                    <button
                                                        key={plan}
                                                        className={`btn btn-style btn-sm${selectedPlanType === plan ? ' active' : ''}`}
                                                        onClick={() => handlePlanTypeChange(plan)}
                                                        id={`transaction-plan-${plan.toLowerCase()}`}
                                                        style={{ textTransform: 'none' }}
                                                    >
                                                        {plan === "All" ? "All Plans" : plan}
                                                    </button>
                                                ))}
                                            </div>
                                            {/* Chart view toggle */}
                                            {/* <div className="btn-group btn-group-sm" role="group" id="transaction-chart-view-toggle">
                                                <button
                                                    type="button"
                                                    className={`btn btn-style btn-sm ${chartViewType === 'status' ? 'active' : ''}`}
                                                    onClick={() => setChartViewType('status')}
                                                    id="chart-view-status"
                                                >
                                                    By Status
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn btn-style btn-sm ${chartViewType === 'plan' ? 'active' : ''}`}
                                                    onClick={() => setChartViewType('plan')}
                                                    id="chart-view-plan"
                                                >
                                                    By Plan Type
                                                </button>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>

                                {/* Bar chart */}
                                <ResponsiveContainer width="100%" height={300} id="transaction-bar-chart">
                                    <BarChart
                                        data={chartData}
                                        barSize={view === 'WEEK' ? 80 : view === 'MONTH' ? 50 : view === 'YEAR' ? 70 : 20}
                                    >
                                        <CartesianGrid strokeDasharray="7 7" vertical={false} />
                                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={70} />
                                        <YAxis />
                                        <Tooltip formatter={(value, name) => [`$${Number(value).toFixed(2)}`, name]} />
                                        {chartViewType === 'status' ? (
                                            STATUS_BARS.map(({ key, label, color }) => (
                                                <Bar key={key} dataKey={key} name={label} stackId="a" fill={color} />
                                            ))
                                        ) : (
                                            PLAN_BARS.map(({ key, label, color }) => (
                                                <Bar key={key} dataKey={key} name={label} stackId="a" fill={color} />
                                            ))
                                        )}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Transaction List Table */}
                            <div className="row table-data" id="transaction-table-data-row">
                                <div className="col-12">
                                    <div className="card" id="transaction-card">
                                        <div className="card-body" id="transaction-card-body">
                                            <DataTable
                                                key={keyForRerender}
                                                columns={columns}
                                                keyField="_id"
                                                data={data}

                                                highlightOnHover
                                                striped
                                                persistTableHead
                                                pagination
                                                paginationServer

                                                subHeaderAlign="right"
                                                noDataComponent={customNoRecords()}

                                                paginationTotalRows={totalCount}
                                                onChangeRowsPerPage={handlePerRowsChange}
                                                onChangePage={handlePageChange}

                                                paginationPerPage={rowsPerPage}
                                                paginationDefaultPage={currentPageNew}

                                                progressPending={isLoading}
                                                progressComponent={<TableLoader />}

                                                // ✅ INLINE STYLE FIX
                                                customStyles={{
                                                    table: {
                                                        style: {
                                                            width: "100%",
                                                            tableLayout: "fixed", // 🔥 stops expansion
                                                        },
                                                    },
                                                    headRow: {
                                                        style: {
                                                            width: "100%",
                                                        },
                                                    },
                                                    rows: {
                                                        style: {
                                                            width: "100%",
                                                        },
                                                    },
                                                    headCells: {
                                                        style: {
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            paddingLeft: "8px",
                                                            paddingRight: "8px",
                                                        },
                                                    },
                                                    cells: {
                                                        style: {
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            paddingLeft: "8px",
                                                            paddingRight: "8px",
                                                        },
                                                    },
                                                }}

                                                id="transaction-data-table"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                    <SweetAlert
                        show={tableErrMsg}
                        custom
                        confirmBtnText="ok"
                        confirmBtnBsStyle="primary"
                        title={"Something went wrong. Please reload again."}
                        onConfirm={() => setTableErrMsg(false)}
                        id="transaction-error-alert"
                    />

                    <Footer id="transaction-footer" />
                </div>
            </div>
        </>
    );
};

export default Transactions;
